/*
We have onLoad: 'check-sso' because we want to trigger the event to login, to be able to change the locale
*/
import jwt_decode from 'jwt-decode'
import { env } from '@shared/config/env'
import { isMerchantSSODomain } from '@uz/config'
import { stripTrailingSlashes } from '@shared/utils/strings'

function getOrigin() {
  return globalThis.location?.origin ?? ''
}

// What Keycloak appends to the address bar when it returns from a login.
const LOGIN_RESPONSE_PARAMS = [
  'code',
  'state',
  'session_state',
  'iss',
  'error',
  'error_description',
  'error_uri'
]

// Returning to the page the login started on is what keeps the user's context.
// A finished login response must not survive into the next `redirect_uri`, or
// Keycloak rejects it as unregistered. Merchant SSO hosts stay on their origin
// root, the only address registered for them.
export function getRedirectUri() {
  const { location } = globalThis

  if (!location || isMerchantSSODomain()) {
    return `${getOrigin()}/`
  }

  const url = new URL(location.href)
  LOGIN_RESPONSE_PARAMS.forEach((param) => url.searchParams.delete(param))
  url.hash = ''

  return url.toString()
}

// keycloak-js only cleans up the fragment, its `responseMode`, so a response
// arriving in the query stays in the address bar and rides along into every
// later request. The fragment is left alone: keycloak-js still reads it.
export function stripLoginResponseFromUrl() {
  const { location, history } = globalThis

  if (!location || !history) {
    return
  }

  const url = new URL(location.href)
  const leftovers = LOGIN_RESPONSE_PARAMS.filter((param) =>
    url.searchParams.has(param)
  )

  if (leftovers.length === 0) {
    return
  }

  leftovers.forEach((param) => url.searchParams.delete(param))
  history.replaceState(history.state, '', url.toString())
}

// keycloak-js reads the fragment during init, so anything rewriting the address
// before then has to leave a waiting response alone or the login is lost.
export function hasPendingLoginResponse() {
  const hash = globalThis.location?.hash

  if (!hash) {
    return false
  }

  const params = new URLSearchParams(hash.slice(1))

  return LOGIN_RESPONSE_PARAMS.some((param) => params.has(param))
}

// The only page Endor links a support agent to, and the parameter that link
// always carries.
const SUPPORT_ENTRY_PATH = '/login/support'
const SUPPORT_ENTRY_PARAM = 'client_account'

// The broker Endor sends support agents through, named as Keycloak knows it.
const SUPPORT_IDP_ALIAS = 'azure-ad'

const SUPPORT_LOGIN_ATTEMPT_KEY = 'support_idp_login_attempted'

function isSupportEntryLanding(location) {
  return (
    location?.pathname === SUPPORT_ENTRY_PATH &&
    new URLSearchParams(location.search).has(SUPPORT_ENTRY_PARAM)
  )
}

// `state` marks a login the adapter started and can finish. Endor builds its
// own authorize request, so its response has none: keycloak-js cannot exchange
// that code, and a code we never asked for could have been planted by anyone.
// Scoped to Endor's landing so a response still to be processed elsewhere
// survives. Run before `createWebHistory` reads the address.
export function discardUnboundSupportLoginResponse() {
  const { location, history } = globalThis

  if (!location || !history || !isSupportEntryLanding(location)) {
    return
  }

  const response = new URLSearchParams(location.hash?.slice(1) ?? '')

  if (!response.has('code') || response.has('state')) {
    return
  }

  const url = new URL(location.href)
  url.hash = ''
  history.replaceState(history.state, '', url.toString())
}

// Keycloak involves the broker only when asked, and shows its own password form
// otherwise, which a support agent has no answer for. Hinted once per landing,
// so a broker that refuses them ends at that form instead of in a redirect
// loop. No sessionStorage means no way to record the attempt, so no hint.
export function consumeSupportIdpHint() {
  const { location, sessionStorage } = globalThis

  if (!isSupportEntryLanding(location) || !sessionStorage) {
    return undefined
  }

  try {
    if (sessionStorage.getItem(SUPPORT_LOGIN_ATTEMPT_KEY)) {
      return undefined
    }

    sessionStorage.setItem(SUPPORT_LOGIN_ATTEMPT_KEY, '1')
    return SUPPORT_IDP_ALIAS
  } catch {
    return undefined
  }
}

// Lets a session expiring later in the same tab renew through the broker again.
export function clearSupportIdpHint() {
  globalThis.sessionStorage?.removeItem(SUPPORT_LOGIN_ATTEMPT_KEY)
}

// Storage keeps strings, so a token stored before it existed reads back as the
// word "undefined".
const ABSENT_TOKEN_VALUES = new Set(['undefined', 'null', ''])

function readStoredToken(key) {
  const value = globalThis.localStorage?.getItem(key)

  return ABSENT_TOKEN_VALUES.has(value) ? null : value
}

const STORED_TOKEN_KEYS = ['token', 'refreshToken', 'idToken', 'token2FA']

const STORED_SESSION_KEYS = [
  ...STORED_TOKEN_KEYS,
  'token2FACreationDate',
  'keycloakUserId',
  'clientAccountId',
  'expires_at',
  'support_client_account'
]

function getTokenIssuerHost(token) {
  try {
    return new URL(jwt_decode(token)?.iss).host
  } catch {
    return null
  }
}

function getConfiguredKeycloakHost() {
  try {
    return new URL(env.keycloakUrl).host
  } catch {
    return null
  }
}

// Only the issuer host separates environments, since they all name their realm
// the same. With no host configured there is nothing to compare against, and a
// token that may be usable beats none at all.
function readCurrentEnvironmentToken(key) {
  const token = readStoredToken(key)
  const configuredHost = getConfiguredKeycloakHost()

  if (!token || !configuredHost) {
    return token
  }

  return getTokenIssuerHost(token) === configuredHost ? token : null
}

// Storage belongs to the origin, so localhost keeps whatever the last build
// signed in to. One foreign token condemns the set: what is left would describe
// a session, and a merchant, that never existed here.
export function discardForeignKeycloakSession() {
  const stored = STORED_TOKEN_KEYS.map(readStoredToken).filter(Boolean)
  const configuredHost = getConfiguredKeycloakHost()

  if (
    !configuredHost ||
    stored.every((token) => getTokenIssuerHost(token) === configuredHost)
  ) {
    return
  }

  STORED_SESSION_KEYS.forEach((key) => globalThis.localStorage?.removeItem(key))
}

// Both tokens or none, as an access token alone cannot be renewed.
function getStoredKeycloakTokens() {
  const token = readCurrentEnvironmentToken('token')
  const refreshToken = readCurrentEnvironmentToken('refreshToken')
  const idToken = readCurrentEnvironmentToken('idToken')

  if (!token || !refreshToken) {
    return {}
  }

  return {
    token,
    refreshToken,
    ...(idToken ? { idToken } : {})
  }
}

export function getKeycloakConfig() {
  return {
    initOptions: {
      flow: 'standard',
      pkceMethod: 'S256',
      checkLoginIframe: false,
      onLoad: 'check-sso',
      redirectUri: getRedirectUri(),
      ...getStoredKeycloakTokens()
    },
    config: {
      url: stripTrailingSlashes(env.keycloakUrl),
      realm: env.keycloakRealm,
      clientId: env.keycloakClient
    }
  }
}
